/**
 * 🎵 SMART MELODY SYSTEM
 * Système de touches musicales intelligentes pour AppsHub
 * Gamme pentatonique = toujours harmonieux !
 * 
 * Instruments disponibles:
 * - 🎷 Saxophone
 * - 🎹 Piano
 * - 🎸 Guitare électrique
 * - 🎻 Violon
 * - 🎺 Trompette
 * - 🥁 Batterie
 * - 🪘 Congas
 * - 🎵 Clarinette
 */

// Gamme pentatonique majeure de C (toujours harmonieux!)
const PENTATONIC_SCALE = {
  C4: 261.63,
  D4: 293.66,
  E4: 329.63,
  G4: 392.00,
  A4: 440.00,
  C5: 523.25,
  D5: 587.33,
  E5: 659.25,
};

// Notes assignées à chaque catégorie d'apps
const CATEGORY_NOTES = {
  'betting': ['C4', 'E4'],      // All-in ou rien ! - Classique
  'social': ['D4', 'G4'],       // Squad Goals - Joyeux
  'music': ['E4', 'A4'],        // Pump le son ! - Mélodieux
  'video': ['G4', 'C5'],        // Mode canapé ON - Relaxant
  'shopping': ['A4', 'D5'],     // Shut up & take my money - Énergique
  'food': ['C5', 'E5'],         // Ventre qui gargouille - Gourmand
  'tools': ['D5', 'G4'],        // Abracadabra - Magique
  'transport': ['E5', 'A4'],    // Vroum vroum ! - Rapide
  'pro': ['C4', 'G4'],          // Mode Boss activé - Professionnel & puissant
  'ai': ['E4', 'C5'],           // Cerveaux augmentés - Futuriste
  'crypto': ['A4', 'E5'],       // Wallet to the moon - Ascendant 🚀
};

// Configuration des instruments avec leurs caractéristiques sonores
const INSTRUMENTS = {
  saxophone: {
    name: 'Saxophone',
    emoji: '🎷',
    type: 'sawtooth',
    attack: 0.05,
    decay: 0.2,
    sustain: 0.6,
    release: 0.3,
    filterFreq: 2000,
    filterQ: 2,
    vibrato: true,
    vibratoSpeed: 5,
    vibratoDepth: 3,
  },
  piano: {
    name: 'Piano',
    emoji: '🎹',
    type: 'triangle',
    attack: 0.01,
    decay: 0.3,
    sustain: 0.4,
    release: 0.5,
    filterFreq: 5000,
    filterQ: 1,
    vibrato: false,
  },
  guitar: {
    name: 'Guitare électrique',
    emoji: '🎸',
    type: 'square',
    attack: 0.01,
    decay: 0.1,
    sustain: 0.5,
    release: 0.4,
    filterFreq: 3000,
    filterQ: 3,
    vibrato: true,
    vibratoSpeed: 6,
    vibratoDepth: 2,
  },
  violin: {
    name: 'Violon',
    emoji: '🎻',
    type: 'sawtooth',
    attack: 0.1,
    decay: 0.1,
    sustain: 0.8,
    release: 0.3,
    filterFreq: 4000,
    filterQ: 1.5,
    vibrato: true,
    vibratoSpeed: 5.5,
    vibratoDepth: 4,
  },
  trumpet: {
    name: 'Trompette',
    emoji: '🎺',
    type: 'sawtooth',
    attack: 0.02,
    decay: 0.1,
    sustain: 0.7,
    release: 0.2,
    filterFreq: 2500,
    filterQ: 4,
    vibrato: true,
    vibratoSpeed: 4,
    vibratoDepth: 2,
  },
  drums: {
    name: 'Batterie',
    emoji: '🥁',
    type: 'noise',
    attack: 0.001,
    decay: 0.15,
    sustain: 0.1,
    release: 0.1,
    filterFreq: 1000,
    filterQ: 1,
    vibrato: false,
    isPercussion: true,
  },
  congas: {
    name: 'Congas',
    emoji: '🪘',
    type: 'sine',
    attack: 0.001,
    decay: 0.2,
    sustain: 0.05,
    release: 0.15,
    filterFreq: 800,
    filterQ: 2,
    vibrato: false,
    isPercussion: true,
    pitchBend: true,
  },
  clarinet: {
    name: 'Clarinette',
    emoji: '🎵',
    type: 'square',
    attack: 0.04,
    decay: 0.15,
    sustain: 0.7,
    release: 0.25,
    filterFreq: 1800,
    filterQ: 3,
    vibrato: true,
    vibratoSpeed: 5,
    vibratoDepth: 2.5,
  },
};

// Classe principale SmartMelody
class SmartMelodyEngine {
  constructor() {
    this.audioContext = null;
    this.currentInstrument = this.loadInstrumentPreference();
    this.isEnabled = this.loadEnabledPreference();
    this.lastPlayedNotes = [];
    this.masterGain = null;
  }

  // Initialise le contexte audio
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = 0.3;
      this.masterGain.connect(this.audioContext.destination);
    }
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  // Charge la préférence d'instrument
  loadInstrumentPreference() {
    try {
      return localStorage.getItem('smartmelody_instrument') || 'saxophone';
    } catch (e) {
      return 'saxophone';
    }
  }

  // Sauvegarde la préférence d'instrument
  saveInstrumentPreference(instrument) {
    this.currentInstrument = instrument;
    try {
      localStorage.setItem('smartmelody_instrument', instrument);
    } catch (e) {}
  }

  // Charge la préférence activé/désactivé
  loadEnabledPreference() {
    try {
      return localStorage.getItem('smartmelody_enabled') !== 'false';
    } catch (e) {
      return true;
    }
  }

  // Sauvegarde la préférence activé/désactivé
  saveEnabledPreference(enabled) {
    this.isEnabled = enabled;
    try {
      localStorage.setItem('smartmelody_enabled', enabled.toString());
    } catch (e) {}
  }

  // Crée un bruit blanc pour la batterie
  createNoiseBuffer() {
    const bufferSize = this.audioContext.sampleRate * 0.5;
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const output = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }

  // Joue une note avec l'instrument sélectionné
  playNote(frequency, instrument) {
    if (!this.isEnabled) return;
    
    this.init();
    const config = INSTRUMENTS[instrument];
    const ctx = this.audioContext;
    const now = ctx.currentTime;

    if (config.isPercussion) {
      this.playPercussion(frequency, config);
      return;
    }

    // Oscillateur principal
    const osc = ctx.createOscillator();
    osc.type = config.type;
    osc.frequency.value = frequency;

    // Filtre pour façonner le son
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = config.filterFreq;
    filter.Q.value = config.filterQ;

    // Enveloppe ADSR
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.5, now + config.attack);
    gainNode.gain.linearRampToValueAtTime(config.sustain * 0.5, now + config.attack + config.decay);
    gainNode.gain.linearRampToValueAtTime(0, now + config.attack + config.decay + config.release);

    // Vibrato optionnel
    if (config.vibrato) {
      const vibrato = ctx.createOscillator();
      const vibratoGain = ctx.createGain();
      vibrato.type = 'sine';
      vibrato.frequency.value = config.vibratoSpeed;
      vibratoGain.gain.value = config.vibratoDepth;
      vibrato.connect(vibratoGain);
      vibratoGain.connect(osc.frequency);
      vibrato.start(now);
      vibrato.stop(now + config.attack + config.decay + config.release + 0.1);
    }

    // Connexions
    osc.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.masterGain);

    // Démarrage et arrêt
    osc.start(now);
    osc.stop(now + config.attack + config.decay + config.release + 0.1);
  }

  // Joue une percussion
  playPercussion(frequency, config) {
    const ctx = this.audioContext;
    const now = ctx.currentTime;

    if (config.type === 'noise') {
      // Batterie - bruit
      const noise = ctx.createBufferSource();
      noise.buffer = this.createNoiseBuffer();
      
      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = 'lowpass';
      noiseFilter.frequency.value = config.filterFreq;
      
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.4, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
      
      noise.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(this.masterGain);
      
      noise.start(now);
      noise.stop(now + 0.3);

      // Kick drum
      const kick = ctx.createOscillator();
      kick.type = 'sine';
      kick.frequency.setValueAtTime(150, now);
      kick.frequency.exponentialRampToValueAtTime(50, now + 0.1);
      
      const kickGain = ctx.createGain();
      kickGain.gain.setValueAtTime(0.6, now);
      kickGain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
      
      kick.connect(kickGain);
      kickGain.connect(this.masterGain);
      kick.start(now);
      kick.stop(now + 0.2);
    } else {
      // Congas
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(frequency * 1.5, now);
      
      if (config.pitchBend) {
        osc.frequency.exponentialRampToValueAtTime(frequency * 0.5, now + 0.15);
      }
      
      const gainNode = ctx.createGain();
      gainNode.gain.setValueAtTime(0.5, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
      
      osc.connect(gainNode);
      gainNode.connect(this.masterGain);
      osc.start(now);
      osc.stop(now + 0.3);
    }
  }

  // Joue les notes pour une catégorie (appelé au clic sur un bouton)
  playCategoryMelody(categoryId) {
    if (!this.isEnabled) return;
    
    const notes = CATEGORY_NOTES[categoryId];
    if (!notes) return;

    // Joue les 2 notes avec un léger décalage pour créer une mélodie
    notes.forEach((noteName, index) => {
      const frequency = PENTATONIC_SCALE[noteName];
      setTimeout(() => {
        this.playNote(frequency, this.currentInstrument);
      }, index * 100); // 100ms entre chaque note
    });

    // Ajoute une harmonique bonus aléatoire pour plus de fun
    const allNotes = Object.keys(PENTATONIC_SCALE);
    const bonusNote = allNotes[Math.floor(Math.random() * allNotes.length)];
    setTimeout(() => {
      this.playNote(PENTATONIC_SCALE[bonusNote] * 0.5, this.currentInstrument); // Octave inférieure
    }, 200);
  }

  // Retourne la liste des instruments disponibles
  getInstruments() {
    return INSTRUMENTS;
  }

  // Retourne l'instrument actuel
  getCurrentInstrument() {
    return this.currentInstrument;
  }

  // Retourne si activé
  getIsEnabled() {
    return this.isEnabled;
  }
}

// Instance singleton
const smartMelody = new SmartMelodyEngine();

export default smartMelody;
export { INSTRUMENTS, CATEGORY_NOTES, PENTATONIC_SCALE };
